<template>
  <div>
    <!-- 기본정보 -->
    <field-select-module
      v-model="form.stdInfo"
      :board="board"
      :is-print="isPrint"
      :options="[{ type: 'text', label: '문서번호', readonly: true, visibleWhenValue: true }]"
    />

    <!-- 개요 -->
    <div class="mt-28px">
      <h5 class="mb-4"><i class="dot"></i> 개요</h5>
      <div class="writeGridContainer">
        <div>
          <div>발생일자</div>
          <p v-if="isPrint" class="print-input">{{ form.발생일자 }}</p>
          <date-selector v-else v-model="form.발생일자" :readonly="board.readonly" placeholder="날짜선택" />
        </div>
        <div class="writeGridLineItem">
          <div>구분 1</div>
          <p v-if="isPrint" class="print-input">{{ form.구분1 }}</p>
          <div v-else class="h-full flex items-center">
            <radio-group v-model="form.구분1" flex :readonly="board.readonly" :items="['직영', '사내수급', '사외수급']" />
          </div>
        </div>
        <div class="writeGridLineItem">
          <div>구분 2</div>
          <p v-if="isPrint" class="print-input">{{ form.구분2 }}</p>
          <div v-else class="h-full flex items-center">
            <radio-group v-model="form.구분2" flex :readonly="board.readonly" :items="['개별', '단체']" />
          </div>
        </div>
        <div>
          <div class="flex items-center">
            등급
            <feather-icon v-if="!isPrint" icon="InfoIcon" svgClasses="h-5 w-5" class="info-icon" @click="showGradePopup = true" />
          </div>
          <p v-if="isPrint" class="print-input">{{ form.등급 }}</p>
          <vs-select v-else autocomplete class="w-full" v-model="form.등급" :disabled="board.readonly" placeholder="경미, 주의, 심각">
            <vs-select-item text="경미 (-1점)" value="경미" />
            <vs-select-item text="주의 (-2점)" value="주의" />
            <vs-select-item text="심각 (-3점)" value="심각" />
          </vs-select>
        </div>
        <div>
          <div>부서명/업체명</div>
          <p v-if="isPrint" class="print-input">{{ 부서명업체명 }}</p>
          <company-selector
            v-else-if="form.구분2 === '단체'"
            v-model="form.업체.id"
            :company-name="form.업체.업체명"
            :readonly="board.readonly"
            placeholder="선택"
            @select="onSelectCompany"
          />
          <ml-input v-else :value="부서명업체명" :readonly="true" placeholder="이름 선택 시 자동입력" />
        </div>
        <div v-if="form.구분2 === '개별'">
          <div>파트명</div>
          <p v-if="isPrint" class="print-input">{{ form.대상자.파트명 }}</p>
          <ml-input v-else v-model="form.대상자.파트명" :readonly="true" placeholder="자동입력" />
        </div>
        <div v-if="form.구분2 === '개별'">
          <div>직급</div>
          <p v-if="isPrint" class="print-input">{{ form.대상자.직급 }}</p>
          <ml-input v-else v-model="form.대상자.직급" :readonly="true" placeholder="자동입력" />
        </div>
        <div v-if="form.구분2 === '개별'">
          <div>이름</div>
          <p v-if="isPrint" class="print-input">{{ form.대상자.이름 }}</p>
          <member-selector
            v-else
            popup-type="user"
            v-model="form.대상자.id"
            :member-name="form.대상자.이름"
            :readonly="board.readonly"
            @select="onSelectMember"
          />
        </div>
        <div>
          <div>발급일자</div>
          <p v-if="isPrint" class="print-input">{{ form.발급일자 }}</p>
          <date-selector v-else v-model="form.발급일자" :readonly="board.readonly" placeholder="날짜선택 (필수)" />
        </div>
      </div>
    </div>

    <!-- 위반정보 -->
    <div class="mt-28px">
      <h5 class="mb-4"><i class="dot"></i> 위반정보</h5>
      <div class="writeGridContainer">
        <div>
          <div class="flex items-center">
            위반 내역
            <feather-icon v-if="!isPrint" icon="InfoIcon" svgClasses="h-5 w-5" class="info-icon" @click="showViolationPopup = true" />
          </div>
          <p v-if="isPrint" class="print-input">{{ form.위반내역.유형 }}</p>
          <vs-select v-else autocomplete class="w-full" v-model="form.위반내역.유형" :disabled="board.readonly" placeholder="선택" @input="onChangeViolationType">
            <vs-select-item v-for="category in 위반유형목록" :key="category.유형" :text="category.유형" :value="category.유형" />
          </vs-select>
        </div>
        <div class="writeGridLineItem">
          <div>세부항목</div>
          <p v-if="isPrint" class="print-input">{{ form.위반내역.항목 }}</p>
          <vs-select v-else autocomplete class="w-full" v-model="form.위반내역.항목" :disabled="board.readonly || !form.위반내역.유형" placeholder="위반 내역을 먼저 선택해주세요">
            <vs-select-item v-for="(item, itemIndex) in 세부항목목록" :key="`item-${itemIndex}`" :text="item" :value="item" />
          </vs-select>
        </div>
        <div class="writeGridLineItem">
          <div>세부설명</div>
          <p v-if="isPrint" class="print-input">{{ form.세부설명 }}</p>
          <ml-textarea v-else class="flex-1 w-full" rows="5" v-model="form.세부설명" :max="2000" :readonly="board.readonly" placeholder="세부설명" />
        </div>
      </div>
    </div>

    <!-- 파일첨부 -->
    <file-upload-module id="fileList" class="w-full mt-28px" v-model="form.fileList" :is-print="isPrint" :readonly="board.readonly" />

    <!-- 기준 팝업 -->
    <popup-grade-criteria v-model="showGradePopup" />
    <popup-violation-criteria v-model="showViolationPopup" />
  </div>
</template>

<script>
import FieldSelectModule from "@/views/modules/FieldSelectModule";
import FileUploadModule from "@/views/modules/FileUploadModule.vue";
import MemberSelector from "@/components/selector/MemberSelector.vue";
import CompanySelector from "@/components/selector/CompanySelector.vue";
import DateSelector from "@/components/selector/DateSelector.vue";
import RadioGroup from "@/components/radio/RadioGroup.vue";
import PopupGradeCriteria from "./PopupGradeCriteria.vue";
import PopupViolationCriteria from "./PopupViolationCriteria.vue";
import { 위반유형목록 } from "./violationData";
import { registerTypeList } from "@/views/pages/register/RegisterTypeInfo.js";

// 구분1 자동 선택용 판별 기준 (830/List.js 의 작성자 구분 검색과 동일한 규칙)
// - 직영 : 소속 업체 id 가 914~917 (본사 / 공장·연구소 안전환경팀 / 관리감독자 / 일반직원)
// - 사내수급 : 수급업체 소속이면서 회원구분(registerType) = 사내담당자
// - 사외수급 : 수급업체 소속이면서 그 외 회원구분
const 직영업체Ids = [914, 915, 916, 917];
const 사내담당자 = registerTypeList.find((e) => e.key === "사내수급업체문서담당자").value;

export default {
  components: {
    FieldSelectModule,
    FileUploadModule,
    MemberSelector,
    CompanySelector,
    DateSelector,
    RadioGroup,
    PopupGradeCriteria,
    PopupViolationCriteria,
  },
  props: {
    isPrint: Boolean,
    board: Object,
    value: Object,
  },
  computed: {
    세부항목목록() {
      const category = 위반유형목록.find((item) => item.유형 === this.form.위반내역.유형);
      return category ? category.항목 : [];
    },
    부서명업체명() {
      if (this.form.구분1 === "직영") {
        return this.form.대상자.부서명 || this.form.업체.업체명 || "";
      }
      return this.form.업체.업체명 || this.form.대상자.부서명 || "";
    },
  },
  data() {
    return {
      위반유형목록,
      showGradePopup: false,
      showViolationPopup: false,
      form: {
        stdInfo: {},
        발생일자: "",
        구분1: "직영",
        구분2: "개별",
        등급: "",
        대상자: {
          id: 0,
          이름: "",
          사번: "",
          부서명: "",
          파트명: "",
          직급: "",
          업체id: 0, // 결재선 수신참조 자동 추가 시 사용
          업체명: "",
        },
        업체: {
          id: 0,
          업체명: "",
          사업자등록번호: "",
        },
        위반내역: {
          유형: "",
          항목: "",
        },
        세부설명: "",
        발급일자: "",
        fileList: [],
      },
    };
  },
  watch: {
    value: {
      immediate: true,
      deep: true,
      handler(value) {
        const keys = Object.keys(this.form);
        for (const key of keys) {
          if (value[key] !== undefined && value[key] !== null) {
            this.form[key] = value[key];
          }
        }
      },
    },
    form: {
      immediate: true,
      deep: true,
      handler(value) {
        this.$emit("input", value);
      },
    },
  },
  methods: {
    onSelectMember(member) {
      if (!member) return;
      this.form.대상자.id = member.id;
      this.form.대상자.이름 = member.name || "";
      this.form.대상자.사번 = member.username || "";
      this.form.대상자.부서명 = member.section || "";
      this.form.대상자.파트명 = member.department || "";
      this.form.대상자.직급 = member.rank || "";
      this.form.대상자.업체id = member.company ? member.company.id || 0 : member.company_id || 0;
      this.form.대상자.업체명 = member.company ? member.company.com_name || "" : member.company_name || "";
      if (member.company) {
        this.form.업체.id = member.company.id || 0;
        this.form.업체.업체명 = member.company.com_name || "";
        this.form.업체.사업자등록번호 = member.company.com_number || "";
      }
      // 구분1 자동 선택 (판별 불가하면 기존 값 유지, 라디오로 수정 가능)
      const 구분1 = this.guess구분1(member.company ? member.company.id : member.company_id, member.metas);
      if (구분1) this.form.구분1 = 구분1;
    },
    onSelectCompany(company) {
      if (!company) return;
      this.form.업체.id = company.id || 0;
      this.form.업체.업체명 = company.com_name || "";
      this.form.업체.사업자등록번호 = company.com_number || "";
      // 단체 발부는 개인 대상자 정보를 비운다
      this.form.대상자 = { id: 0, 이름: "", 사번: "", 부서명: "", 파트명: "", 직급: "", 업체id: 0, 업체명: "" };
      // 직영 업체를 고르면 직영으로. 수급업체는 업체 정보만으로 사내/사외를 알 수 없어 기존 값 유지
      if (직영업체Ids.includes(Number(company.id))) {
        this.form.구분1 = "직영";
      } else if (this.form.구분1 === "직영") {
        this.form.구분1 = "사외수급";
      }
    },
    // 소속 업체 id 와 회원 metas(registerType) 로 구분1 을 추정한다. 판별 불가 시 빈 문자열
    guess구분1(companyId, metas) {
      const id = Number(companyId) || 0;
      if (직영업체Ids.includes(id)) return "직영";
      const registerMeta = Array.isArray(metas) ? metas.find((meta) => meta.meta_key === "registerType") : null;
      const registerType = registerMeta ? registerMeta.meta_value : "";
      if (registerType === 사내담당자) return "사내수급";
      if (registerType || id > 0) return "사외수급";
      return "";
    },
    onChangeViolationType() {
      // 위반 내역(대분류) 변경 시 세부항목 초기화
      this.form.위반내역.항목 = "";
    },
    validate() {
      if (!this.form.stdInfo || !this.form.stdInfo.field_name) {
        return "사업장명을 선택해주세요.";
      }
      if (!this.form.발생일자) {
        return "발생일자를 선택해주세요.";
      }
      if (!this.form.등급) {
        return "등급을 선택해주세요.";
      }
      if (this.form.구분2 === "개별" && !this.form.대상자.이름) {
        return "대상자(이름)를 선택해주세요.";
      }
      if (this.form.구분2 === "단체" && !this.form.업체.업체명) {
        return "부서명/업체명을 선택해주세요.";
      }
      if (!this.form.위반내역.유형) {
        return "위반 내역을 선택해주세요.";
      }
      if (!this.form.위반내역.항목) {
        return "위반 세부항목을 선택해주세요.";
      }
      // 발급일자는 작성자가 직접 입력(필수). 결재자 차례(수정 불가)에는 수동 입력 전환 이전 문서가 막히지 않도록 검사하지 않는다
      if (!this.form.발급일자 && !(this.board && this.board.isTurnApproval)) {
        return "발급일자를 선택해주세요.";
      }
    },
    // 결재 최종 승인 시 문서번호 자동 입력 (발급일자는 작성자가 직접 입력, 연도는 발급일자 기준)
    async prevSaveEvent(post) {
      try {
        if (this.isFinalApprovalTurn() && post.body) {
          // 발급일자 수동 입력 전환 이전에 상신된 문서(발급일자 없음)는 기존 방식대로 최종 승인일로 채운다
          if (!post.body.발급일자) {
            post.body.발급일자 = new Date().format("yyyy-MM-dd");
          }
          if (post.body.stdInfo && !post.body.stdInfo.문서번호) {
            post.body.stdInfo.문서번호 = await this.generateDocNumber(post, post.body.발급일자);
          }
        }
      } catch (e) {
        /* ignore */
      }
      return post;
    },
    isFinalApprovalTurn() {
      if (!this.board || !this.board.isTurnApproval) return false;
      const line = this.board.approvalLine || [];
      // 처리부서 결재 이력은 line에 document_company_id 가 붙어서 들어오므로 일반 결재선과 분리한다
      const approvals = line.filter((member) => member.type_code === "approval" && !member.document_company_id);
      const documentCompany = this.board.documentCompany;
      const hasDocumentCompany = !!(documentCompany && (documentCompany.document_company_id || documentCompany.virtual_company_id));
      const documentCompanyDone = line.some((member) => member.document_company_id);

      if (hasDocumentCompany && !documentCompanyDone) {
        // 처리부서 결재가 마지막 단계: 일반 결재선 전원 승인 + 내(처리부서) 차례일 때가 최종 승인
        return approvals.length > 0 && approvals.every((member) => parseInt(member.status_id) === 2);
      }

      // 처리부서가 없으면 일반 결재선의 마지막 결재자 승인이 최종
      if (approvals.length === 0) return false;
      const last = approvals[approvals.length - 1];
      const myId = this.$store.state.auth.userInfo.id;
      if (parseInt(last.user_id) !== parseInt(myId)) return false;
      return approvals.slice(0, -1).every((member) => parseInt(member.status_id) === 2);
    },
    async generateDocNumber(post, issueDate) {
      const year = `${issueDate}`.substring(0, 4);
      const dept = (post.body.stdInfo && (post.body.stdInfo.section || post.body.stdInfo.department)) || "";
      const prefix = `DKS-안전위반통보서-${year}-${dept}-`;
      let seq = 1;
      try {
        // 같은 접두어의 기존 문서번호 중 최대값 + 1 로 채번
        const response = await this.$store.dispatch("board/LOAD_POST_LIST", {
          projectId: parseInt(this.$route.params.proj_id),
          boardId: parseInt(this.$route.params.boardId) || 1229,
          page: 1,
          perPage: 1,
          conditions: [{ key: "body.stdInfo.문서번호", operator: "LIKE", value: `${prefix}%` }],
          orderTarget: "$body.stdInfo.문서번호",
          orderDirection: "desc",
          cacheBuster: Date.now(),
        });
        if (response.data && response.data.length > 0) {
          const body = JSON.parse(response.data[0].post_content).body || {};
          const lastNumber = (body.stdInfo && body.stdInfo.문서번호) || "";
          const lastSeq = parseInt(lastNumber.substring(prefix.length), 10);
          seq = isNaN(lastSeq) ? (response.total || 0) + 1 : lastSeq + 1;
        }
      } catch (e) {
        /* ignore */
      }
      return `${prefix}${String(seq).padStart(3, "0")}`;
    },
  },
};
</script>

<style lang="scss" scoped>
.info-icon {
  color: #7e72f2;
  cursor: pointer;
  margin-left: 6px;
}
</style>
